import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, Cloud, Shield, BarChart3, Code, Cog, 
  Smartphone, Database, Network, Zap 
} from 'lucide-react';

const Services = () => {
  const navigate = useNavigate();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  const services = [
    {
      icon: <Brain />,
      title: 'AI & Machine Learning',
      description: 'Intelligent automation, predictive analytics, and cognitive computing solutions',
      capabilities: ['Computer Vision', 'Natural Language Processing', 'Predictive Analytics', 'Robotic Process Automation'],
      url: '/ai-machine-learning'
    },
    {
      icon: <Cloud />,
      title: 'Cloud Services',
      description: 'End-to-end cloud transformation and modernization services',
      capabilities: ['Cloud Migration', 'Multi-Cloud Strategy', 'Cloud-Native Development', 'DevOps & CI/CD'],
      url: '/cloud-services'
    },
    {
      icon: <Shield />,
      title: 'Cybersecurity',
      description: 'Comprehensive security solutions for digital transformation',
      capabilities: ['Zero Trust Architecture', 'Threat Intelligence', 'Identity Management', 'Compliance & Governance'],
      url: '/cybersecurity'
    },
    {
      icon: <BarChart3 />,
      title: 'Data & Analytics',
      description: 'Transform data into actionable insights and competitive advantage',
      capabilities: ['Big Data Engineering', 'Business Intelligence', 'Data Science', 'Real-time Analytics'],
      url: '/data-analytics'
    },
    {
      icon: <Code />,
      title: 'Application Development',
      description: 'Modern application development and legacy modernization',
      capabilities: ['Microservices Architecture', 'API Management', 'Mobile Development', 'Web Applications'],
      url: '/application-development'
    },
    {
      icon: <Cog />,
      title: 'Digital Transformation',
      description: 'Strategic consulting and implementation for digital-first organizations',
      capabilities: ['Strategy Consulting', 'Process Automation', 'Change Management', 'Innovation Labs'],
      url: '/digital-transformation'
    }
  ];

  return (
    <section id="services" className="services" ref={ref}>
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2>Comprehensive Technology Services</h2>
          <p>
            Empowering enterprises with cutting-edge technology solutions that drive 
            innovation, efficiency, and sustainable growth across industries.
          </p>
        </motion.div>

        <div className="services-grid">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              className="service-card"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <div className="service-icon">
                {service.icon}
              </div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <ul className="capabilities">
                {service.capabilities.map(capability => (
                  <li key={capability}>{capability}</li>
                ))}
              </ul>
              <button className="service-cta" onClick={() => { navigate(service.url); window.scrollTo(0, 0); }}>Learn More</button>
            </motion.div>
          ))}
        </div>

        <div className="services-footer">
          <div className="platform-services">
            <h3>Platform Services</h3>
            <div className="platform-grid">
              <div className="platform-item">
                <Smartphone />
                <span>Mobile Platforms</span>
              </div>
              <div className="platform-item">
                <Database />
                <span>Database Management</span>
              </div>
              <div className="platform-item">
                <Network />
                <span>Network Solutions</span>
              </div>
              <div className="platform-item">
                <Zap />
                <span>Automation Tools</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;